// ═══════════════════════════════════════════════════════════════
// useRealtimeEvents — webhook-driven store refresh
//
// Polls /api/events/poll every 5 s. The endpoint does a handful
// of KV reads and returns only the table IDs touched since the
// last poll. Store reloads fire only when something actually
// changed — zero unnecessary fetches in quiet periods.
//
// The KV entry is written by the GAB webhook receiver at
// /api/gab-webhook whenever GAB fires a subscription event.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useRollCallStore } from '../store/rollcall-store';
import { useHotwashStore } from '../store/hotwash-store';
import { useBattleRhythmStore } from '../store/battlerhythmStore';
import { useGeoOpsStore } from '../store/geoopsStore';
import { useShelterStore } from '../store/shelter-store';
import { useImpactStore } from '../store/impact-store';
import { useIntakeStore } from '../store/intake-store';
import { useHandoffStore } from '../store/handoff-store';

const POLL_MS = 5_000;

// tableId → logical reload keys
const TABLE_MAP: Record<string, string[]> = {
  '0kcnmszg2': ['rc.checkins'],
  'zqarpc156': ['rc.events'],
  'mwudh5hnl': ['auth.incidents'],
  'nsjo6xyee': ['hotwash.observations'],
  '4ustywpyq': ['br.events'],
  'ey3i2ncvn': ['geoops.zones'],
  'udibgvtuy': ['shelter.survivors'],
  '7fgeepu9i': ['intake.individuals'],
  'h2rnhci4m': ['impact.assessments'],
  'ks4ofz9p0': ['volunteer.assignments'],
  'tonw0y1io': ['handoff.notes'],
  'r6szawvdp': ['pagrants.projects'],
};

export function useRealtimeEvents() {
  const lastRef = useRef(new Date(Date.now() - 10_000).toISOString());

  const { loadIncidents, activeIncident } = useAuthStore();
  const { loadCheckins, loadEvents: rcLoadEvents } = useRollCallStore();
  const { loadObservations } = useHotwashStore();
  const { loadEvents: brLoadEvents } = useBattleRhythmStore();
  const { loadZones } = useGeoOpsStore();
  const { loadShelters } = useShelterStore();
  const { loadAssessments } = useImpactStore();
  const { loadIndividuals } = useIntakeStore();
  const { loadAll: loadHandoff } = useHandoffStore();

  const incidentId = activeIncident?.id ? String(activeIncident.id) : null;

  useEffect(() => {
    const poll = async () => {
      try {
        const since = lastRef.current;
        lastRef.current = new Date().toISOString();

        const res = await fetch(`/api/events/poll?since=${encodeURIComponent(since)}`);
        if (!res.ok) return;
        const { changed } = (await res.json()) as { changed: string[] };
        if (changed.length === 0) return;

        const actions = new Set(changed.flatMap((tid) => TABLE_MAP[tid] ?? []));

        if (actions.has('auth.incidents'))       loadIncidents();
        if (actions.has('rc.checkins'))          loadCheckins();
        if (actions.has('rc.events'))            rcLoadEvents();
        if (actions.has('hotwash.observations')) loadObservations();
        if (actions.has('br.events') && incidentId) brLoadEvents(incidentId);
        if (actions.has('geoops.zones') && incidentId) loadZones(incidentId);
        if (actions.has('shelter.survivors'))    loadShelters();
        if (actions.has('intake.individuals'))   loadIndividuals();
        if (actions.has('impact.assessments'))   loadAssessments();
        if (actions.has('handoff.notes'))        loadHandoff();
        // volunteer.assignments — store has no top-level load fn; component-level refresh handles it
      } catch {
        // network error — silent, next tick will retry
      }
    };

    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [incidentId]); // restart if active incident changes
}
